/**
 * Users API Contract
 *
 * ts-rest contract definition for user management endpoints.
 * This contract ensures type safety between frontend and backend.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// ---------------------------------------------------------------------------
// Shared Schemas
// ---------------------------------------------------------------------------

const errorResponseSchema = z.object({
  error: z.object({
    message: z.string(),
    code: z.string().optional(),
  }),
});

const paginationQuerySchema = z.object({
  limit: z
    .string()
    .regex(/^\d+$/, 'Must be a positive integer')
    .optional()
    .default('25')
    .describe('Number of results per page'),
  skip: z
    .string()
    .regex(/^\d+$/, 'Must be a non-negative integer')
    .optional()
    .default('0')
    .describe('Number of results to skip'),
  sortColumn: z.string().max(50).optional().describe('Column to sort by'),
  sortOrder: z.enum(['ASC', 'DESC']).optional().default('ASC').describe('Sort direction'),
});

// ---------------------------------------------------------------------------
// List Users
// ---------------------------------------------------------------------------

export const listUsersQuerySchema = paginationQuerySchema.extend({
  search: z
    .string()
    .max(200)
    .optional()
    .describe('Search by name, email, employee ID, or team name'),
  name: z.string().max(200).optional().describe('Filter by first or second name'),
  email: z.string().max(200).optional().describe('Filter by work or personal email'),
  status: z.enum(['active', 'inactive']).optional().describe('Filter by active/inactive status'),
  accountType: z.enum(['normal', 'third-party']).optional().describe('Filter by account type'),
  isMobileUser: z.enum(['true', 'false']).optional().describe('Filter mobile users'),
  jobTitle: z.string().max(255).optional().describe('Filter by job title'),
  discipline: z.string().max(255).optional().describe('Filter by discipline'),
  team: z.string().max(255).optional().describe('Filter by team'),
  lineManagerId: z.string().regex(/^\d+$/).optional().describe('Filter by line manager ID'),
  activeJobsMin: z.string().regex(/^\d+$/).optional().describe('Minimum active jobs'),
  activeJobsMax: z.string().regex(/^\d+$/).optional().describe('Maximum active jobs'),
  completedJobsMin: z.string().regex(/^\d+$/).optional().describe('Minimum completed jobs'),
  completedJobsMax: z.string().regex(/^\d+$/).optional().describe('Maximum completed jobs'),
});

export const userListItemSchema = z.object({
  id: z.number(),
  firstName: z.string().nullable(),
  secondName: z.string().nullable(),
  workEmail: z.string().nullable(),
  personalEmail: z.string().nullable(),
  workPhone: z.string().nullable(),
  personalPhone: z.string().nullable(),
  jobTitle: z.string().nullable(),
  discipline: z.string().nullable(),
  lineManager: z.string().nullable(),
  lineManagerId: z.number().nullable(),
  team: z.string().nullable(),
  status: z.number().nullable(),
  accountType: z.number().nullable(),
  accountTypeName: z.string().nullable(),
  verified: z.boolean().nullable(),
  leavingVerification: z.boolean().nullable(),
  employeeId: z.string().nullable(),
  profileImageUrl: z.string().nullable(),
  activeJobs: z.number(),
  completedJobs: z.number(),
  isMobileUser: z.boolean(),
  cognitoUsername: z.string().nullable(),
  /** Access-control roles currently assigned to the user. */
  roles: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      isSystem: z.boolean(),
    }),
  ),
});

export const listUsersResponseSchema = z.object({
  users: z.array(userListItemSchema),
  count: z.number(),
});

// ---------------------------------------------------------------------------
// Get User Detail
// ---------------------------------------------------------------------------

export const userDetailSchema = z.object({
  id: z.number(),
  username: z.string().nullable(),
  firstName: z.string().nullable(),
  secondName: z.string().nullable(),
  preferredName: z.string().nullable(),
  personalEmail: z.string().nullable(),
  personalPhone: z.string().nullable(),
  workEmail: z.string().nullable(),
  workPhone: z.string().nullable(),
  dateOfBirth: z.string().nullable(),
  gender: z.string().nullable(),
  maritalStatus: z.string().nullable(),

  // Address
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  addressLine3: z.string().nullable(),
  postcode: z.string().nullable(),

  // Next of Kin
  nextOfKinName: z.string().nullable(),
  nextOfKinSurname: z.string().nullable(),
  nextOfKinRelationship: z.string().nullable(),
  nextOfKinContact: z.string().nullable(),
  nextOfKinAddress: z.string().nullable(),
  nextOfKinPostcode: z.string().nullable(),
  nextOfKinDateOfBirth: z.string().nullable(),

  // Employment
  jobTitle: z.string().nullable(),
  discipline: z.string().nullable(),
  employmentType: z.string().nullable(),
  lineManager: z.string().nullable(),
  lineManagerId: z.number().nullable(),
  teamLeader: z.string().nullable(),
  teamLeaderId: z.number().nullable(),
  supervisor: z.string().nullable(),
  supervisorId: z.number().nullable(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  employeeId: z.string().nullable(),
  ein: z.string().nullable(),
  smartCardNumber: z.string().nullable(),
  company: z.string().nullable(),
  paidVia: z.string().nullable(),

  // Status
  status: z.number().nullable(),
  accountType: z.number().nullable(),
  accountTypeName: z.string().nullable(),
  verified: z.boolean().nullable(),
  leavingVerification: z.boolean().nullable(),
  reasonForLeaving: z.string().nullable(),
  wouldYouReEmploy: z.string().nullable(),

  // Profile
  profileImageUrl: z.string().nullable(),
  cognitoUsername: z.string().nullable(),

  // Roles & Skills
  roles: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
    }),
  ),
  skills: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      disciplineId: z.number().nullable(),
      disciplineName: z.string().nullable(),
    }),
  ),

  // Teams
  teams: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
    }),
  ),

  // Job counts
  activeJobs: z.number(),
  completedJobs: z.number(),

  // Metadata
  createdTime: z.string().nullable(),
  updatedTime: z.string().nullable(),
});

export const getUserDetailResponseSchema = userDetailSchema;

// ---------------------------------------------------------------------------
// Create User
// ---------------------------------------------------------------------------

export const createUserBodySchema = z.object({
  // Personal (Step 1)
  firstName: z.string().min(1, 'First name is required').max(255),
  secondName: z.string().min(1, 'Last name is required').max(255),
  personalEmail: z.string().email('Invalid email address').max(255).optional(),
  workEmail: z.string().email('Invalid email address').max(255).optional(),
  personalPhone: z.string().max(255).optional(),
  workPhone: z.string().max(255).optional(),
  addressLine1: z.string().max(255).optional(),
  addressLine2: z.string().max(255).optional(),
  addressLine3: z.string().max(255).optional(),
  postcode: z.string().max(255).optional(),

  // Job & Role (Step 2)
  jobTitle: z.string().max(255).optional(),
  discipline: z.string().max(255).optional(),
  lineManagerId: z.number().int().positive().optional(),
  accountType: z.number().int().optional(),
  employmentType: z.string().max(255).optional(),
  company: z.string().max(255).optional(),
  startDate: z.string().optional(),
  roleV2Ids: z.array(z.number().int().positive()).optional(),

  // Flags
  isMobileUser: z.boolean().optional().default(false),
  addUser: z.boolean().optional().default(true).describe('Create Cognito account'),
});

export const createUserResponseSchema = z.object({
  message: z.string(),
  userId: z.number(),
  employeeId: z.string().nullable(),
});

// ---------------------------------------------------------------------------
// Mobile Access (entitlements)
// ---------------------------------------------------------------------------

/**
 * A single mobile application the user is entitled to. Sourced from the
 * secondary `skewb_ear` database (mobile user ⇄ client ⇄ app ⇄ role mapping).
 */
export const mobileApplicationSchema = z.object({
  userClientAppRoleId: z.number(),
  appId: z.number(),
  appName: z.string().nullable(),
  clientId: z.number(),
  clientName: z.string().nullable(),
  roleName: z.string().nullable(),
});

export const mobileAccessResponseSchema = z.object({
  /** Works-side account verification flag (from skewb_works.user). */
  verified: z.boolean().nullable(),
  /** True when the user has at least one mobile client/app/role entitlement. */
  isMobileUser: z.boolean(),
  /** Mobile number held against the mobile-user record (secondary DB). */
  mobileNumber: z.string().nullable(),
  /** Whether the user has a Cognito account (required before mobile access). */
  hasCognitoAccount: z.boolean(),
  applications: z.array(mobileApplicationSchema),
});

/**
 * Grant mobile access to an existing user. Client and app are resolved from
 * backend config (MOBILE_DEFAULT_CLIENT_ID / MOBILE_DEFAULT_APP_ID); only the
 * mobile number is supplied by the caller (defaults to the user's stored phone
 * when omitted).
 */
export const addMobileAccessBodySchema = z.object({
  mobileNumber: z.string().max(50).optional(),
});

// ---------------------------------------------------------------------------
// Update User
// ---------------------------------------------------------------------------

export const updateUserBodySchema = z.object({
  firstName: z.string().min(1).max(255).optional(),
  secondName: z.string().min(1).max(255).optional(),
  preferredName: z.string().max(255).optional().nullable(),
  personalEmail: z.string().email().max(255).optional().nullable(),
  workEmail: z.string().email().max(255).optional().nullable(),
  personalPhone: z.string().max(255).optional().nullable(),
  workPhone: z.string().max(255).optional().nullable(),
  dateOfBirth: z.string().optional().nullable(),
  gender: z.string().max(255).optional().nullable(),
  maritalStatus: z.string().max(255).optional().nullable(),
  jobTitle: z.string().max(255).optional().nullable(),
  discipline: z.string().max(255).optional().nullable(),
  employmentType: z.string().max(255).optional().nullable(),
  lineManagerId: z.number().int().positive().optional().nullable(),
  teamLeaderId: z.number().int().positive().optional().nullable(),
  supervisorId: z.number().int().positive().optional().nullable(),
  startDate: z.string().optional().nullable(),
  endDate: z.string().optional().nullable(),
  company: z.string().max(255).optional().nullable(),
  paidVia: z.string().max(255).optional().nullable(),
  accountType: z.number().int().optional().nullable(),
  roleV2Ids: z.array(z.number().int().positive()).optional(),
  reasonForLeaving: z.string().max(255).optional().nullable(),
  wouldYouReEmploy: z.string().max(255).optional().nullable(),
  ein: z.string().max(20).optional().nullable(),
  smartCardNumber: z.string().max(255).optional().nullable(),
});

export const updateUserResponseSchema = z.object({
  message: z.string(),
  userId: z.number(),
});

// ---------------------------------------------------------------------------
// Update Address
// ---------------------------------------------------------------------------

export const updateAddressBodySchema = z.object({
  addressLine1: z.string().max(255).optional().nullable(),
  addressLine2: z.string().max(255).optional().nullable(),
  addressLine3: z.string().max(255).optional().nullable(),
  postcode: z.string().max(255).optional().nullable(),
});

// ---------------------------------------------------------------------------
// Deactivate / Reactivate User
// ---------------------------------------------------------------------------

export const deactivateUserBodySchema = z.object({
  endDate: z.string().min(1, 'Last day of employment is required'),
  reasonForLeaving: z.string().max(255).optional(),
  wouldYouReEmploy: z.enum(['Yes', 'No']).optional(),
});

// ---------------------------------------------------------------------------
// Reset Password
// ---------------------------------------------------------------------------

/**
 * Body for the admin reset-password endpoint. Two modes:
 *
 *   - `email` (default, all environments): triggers an AWS Cognito
 *     AdminResetUserPasswordCommand which emails the target user a temporary
 *     password and forces them through the NEW_PASSWORD_REQUIRED challenge on
 *     next login.
 *   - `direct` (development environment only): admin sets the user's password
 *     directly. The backend rejects this in non-development environments —
 *     it exists because dev users often have unreachable test email addresses.
 *
 * `appName` selects the Cognito CustomMessage Lambda template (logo, subject,
 * body copy). Optional in the schema for legacy callers; works-lite always
 * sends its PascalCase brand key (e.g. SkewbWorksLite) so the right logo
 * renders — without it the Lambda falls through to the Ignite Core defaults
 * and ships a broken/legacy logo.
 */
export const resetPasswordBodySchema = z.union([
  z.object({
    mode: z.literal('email').optional().default('email'),
    appName: z.string().min(1).optional(),
  }),
  z.object({
    mode: z.literal('direct'),
    newPassword: z.string().min(8, 'Password must be at least 8 characters'),
    appName: z.string().min(1).optional(),
  }),
]);

// ---------------------------------------------------------------------------
// User Roles
// ---------------------------------------------------------------------------

export const assignRoleBodySchema = z.object({
  groupName: z.string().min(1, 'Group name is required'),
});

export const userRolesResponseSchema = z.object({
  roles: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      permissions: z.array(z.string()),
    }),
  ),
  cognitoGroups: z.array(z.string()),
});

// ---------------------------------------------------------------------------
// User Counts
// ---------------------------------------------------------------------------

export const userCountsResponseSchema = z.object({
  totalActive: z.number(),
  totalInactive: z.number(),
  totalVerified: z.number(),
  totalUnverified: z.number(),
  totalThirdParty: z.number(),
  totalNormal: z.number(),
});

// ---------------------------------------------------------------------------
// Verification Stats
// ---------------------------------------------------------------------------

export const verificationStatsResponseSchema = z.object({
  totalUsers: z.number(),
  verifiedUsers: z.number(),
  outstandingVerifications: z.number(),
  verificationPercentage: z.number(),
  byDiscipline: z.array(
    z.object({
      discipline: z.string(),
      total: z.number(),
      verified: z.number(),
      percentage: z.number(),
    }),
  ),
});

// ---------------------------------------------------------------------------
// Line Managers
// ---------------------------------------------------------------------------

export const lineManagersQuerySchema = z.object({
  search: z.string().max(200).optional(),
});

export const lineManagerItemSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export const lineManagersResponseSchema = z.object({
  values: z.array(lineManagerItemSchema),
  count: z.number(),
});

// ---------------------------------------------------------------------------
// Lookups (job titles, disciplines)
// ---------------------------------------------------------------------------

export const lookupItemSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export const lookupResponseSchema = z.object({
  values: z.array(lookupItemSchema),
  count: z.number(),
});

// ---------------------------------------------------------------------------
// Contract Definition
// ---------------------------------------------------------------------------

/**
 * Users API Contract
 *
 * Base path: /api/works-lite/users
 */
export const usersContract = c.router({
  // --- Static path endpoints (must come before /:userId to avoid route conflicts) ---

  listUsers: {
    method: 'GET',
    path: '/',
    query: listUsersQuerySchema,
    responses: {
      200: listUsersResponseSchema,
      400: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'List users with pagination, search, and filters',
  },

  createUser: {
    method: 'POST',
    path: '/',
    body: createUserBodySchema,
    responses: {
      201: createUserResponseSchema,
      400: errorResponseSchema,
      409: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Create a new user',
  },

  getUserCounts: {
    method: 'GET',
    path: '/counts',
    responses: {
      200: userCountsResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get aggregate user counts by status and type',
  },

  getVerificationStats: {
    method: 'GET',
    path: '/verification-stats',
    responses: {
      200: verificationStatsResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get user verification statistics with discipline breakdown',
  },

  listLineManagers: {
    method: 'GET',
    path: '/line-managers',
    query: lineManagersQuerySchema,
    responses: {
      200: lineManagersResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'List available line managers for dropdowns',
  },

  listJobTitles: {
    method: 'GET',
    path: '/lookups/job-titles',
    query: lineManagersQuerySchema,
    responses: {
      200: lookupResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'List available job titles for dropdowns',
  },

  listDisciplines: {
    method: 'GET',
    path: '/lookups/disciplines',
    responses: {
      200: lookupResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'List available disciplines for dropdowns',
  },

  createJobTitle: {
    method: 'POST',
    path: '/lookups/job-titles',
    body: z.object({
      name: z.string().min(1).max(50),
    }),
    responses: {
      201: z.object({ id: z.number(), name: z.string() }),
      409: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Create a new job title',
  },

  deleteJobTitle: {
    method: 'DELETE',
    path: '/lookups/job-titles/:id',
    pathParams: z.object({ id: z.string().regex(/^\d+$/) }),
    body: null,
    responses: {
      200: z.object({ success: z.boolean() }),
      500: errorResponseSchema,
    },
    summary: 'Soft-delete a job title',
  },

  // --- Parameterized path endpoints (after static paths) ---

  getUserDetail: {
    method: 'GET',
    path: '/detail/:userId',
    pathParams: z.object({ userId: z.string().regex(/^\d+$/) }),
    responses: {
      200: getUserDetailResponseSchema,
      403: errorResponseSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get detailed user profile by ID',
  },

  // --- Endpoints without UI (ready for future use) ---

  updateUser: {
    method: 'PUT',
    path: '/detail/:userId',
    pathParams: z.object({ userId: z.string().regex(/^\d+$/) }),
    body: updateUserBodySchema,
    responses: {
      200: updateUserResponseSchema,
      400: errorResponseSchema,
      403: errorResponseSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Update user details',
  },

  updateAddress: {
    method: 'PATCH',
    path: '/detail/:userId/address',
    pathParams: z.object({ userId: z.string().regex(/^\d+$/) }),
    body: updateAddressBodySchema,
    responses: {
      200: updateUserResponseSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Update user address fields only',
  },

  deactivateUser: {
    method: 'PATCH',
    path: '/detail/:userId/deactivate',
    pathParams: z.object({ userId: z.string().regex(/^\d+$/) }),
    body: deactivateUserBodySchema,
    responses: {
      200: updateUserResponseSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Deactivate a user account',
  },

  reactivateUser: {
    method: 'PATCH',
    path: '/detail/:userId/reactivate',
    pathParams: z.object({ userId: z.string().regex(/^\d+$/) }),
    body: z.object({}),
    responses: {
      200: updateUserResponseSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Reactivate a deactivated user account',
  },

  resetPassword: {
    method: 'POST',
    path: '/detail/:userId/reset-password',
    pathParams: z.object({ userId: z.string().regex(/^\d+$/) }),
    body: resetPasswordBodySchema,
    responses: {
      200: z.object({ message: z.string() }),
      400: errorResponseSchema,
      403: errorResponseSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Reset user password (sends Cognito reset email by default)',
  },

  getMobileAccess: {
    method: 'GET',
    path: '/detail/:userId/mobile-access',
    pathParams: z.object({ userId: z.string().regex(/^\d+$/) }),
    responses: {
      200: mobileAccessResponseSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get a user’s mobile application access / entitlements',
  },

  addMobileAccess: {
    method: 'POST',
    path: '/detail/:userId/mobile-access',
    pathParams: z.object({ userId: z.string().regex(/^\d+$/) }),
    body: addMobileAccessBodySchema,
    responses: {
      200: mobileAccessResponseSchema,
      400: errorResponseSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Grant the default mobile application access to a user',
  },

  getUserRoles: {
    method: 'GET',
    path: '/detail/:userId/roles',
    pathParams: z.object({ userId: z.string().regex(/^\d+$/) }),
    responses: {
      200: userRolesResponseSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get user roles and permissions',
  },

  assignRole: {
    method: 'POST',
    path: '/detail/:userId/roles',
    pathParams: z.object({ userId: z.string().regex(/^\d+$/) }),
    body: assignRoleBodySchema,
    responses: {
      200: z.object({ message: z.string() }),
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Assign a Cognito group/role to a user',
  },

  removeRole: {
    method: 'DELETE',
    path: '/detail/:userId/roles/:groupName',
    pathParams: z.object({
      userId: z.string().regex(/^\d+$/),
      groupName: z.string().min(1),
    }),
    body: z.object({}),
    responses: {
      200: z.object({ message: z.string() }),
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Remove a Cognito group/role from a user',
  },
});

// ---------------------------------------------------------------------------
// Type Exports
// ---------------------------------------------------------------------------

export type ListUsersQuery = z.infer<typeof listUsersQuerySchema>;
export type UserListItem = z.infer<typeof userListItemSchema>;
export type ListUsersResponse = z.infer<typeof listUsersResponseSchema>;
export type UserDetail = z.infer<typeof userDetailSchema>;
export type CreateUserBody = z.infer<typeof createUserBodySchema>;
export type CreateUserResponse = z.infer<typeof createUserResponseSchema>;
export type UpdateUserBody = z.infer<typeof updateUserBodySchema>;
export type UpdateUserResponse = z.infer<typeof updateUserResponseSchema>;
export type UpdateAddressBody = z.infer<typeof updateAddressBodySchema>;
export type DeactivateUserBody = z.infer<typeof deactivateUserBodySchema>;
export type ResetPasswordBody = z.infer<typeof resetPasswordBodySchema>;
export type MobileApplication = z.infer<typeof mobileApplicationSchema>;
export type MobileAccessResponse = z.infer<typeof mobileAccessResponseSchema>;
export type AddMobileAccessBody = z.infer<typeof addMobileAccessBodySchema>;
export type AssignRoleBody = z.infer<typeof assignRoleBodySchema>;
export type UserRolesResponse = z.infer<typeof userRolesResponseSchema>;
export type UserCountsResponse = z.infer<typeof userCountsResponseSchema>;
export type VerificationStatsResponse = z.infer<typeof verificationStatsResponseSchema>;
export type LineManagerItem = z.infer<typeof lineManagerItemSchema>;
export type LineManagersResponse = z.infer<typeof lineManagersResponseSchema>;
export type LookupItem = z.infer<typeof lookupItemSchema>;
export type LookupResponse = z.infer<typeof lookupResponseSchema>;
